import { functionalTrainingGridConfig, LOCAL_STORAGE_KEY } from '@core/configs';
import { normalizeSettingHeaders, NormalizeSettingHeadersOptions } from '@core/helper';
import { CommonHelper } from '@core/services/common';

type PrepareHeaderFunction = (header: any) => any;

// Public helpers used by the TV setting menu.
export function getFunctionalTrainingSetting(settingData: any, prepareHeader: PrepareHeaderFunction): any {
    const screenSetting = getFunctionalTrainingScreenSetting(settingData);
    const mergedHeaders = mergeFunctionalTrainingHeaders(
        screenSetting?.headers,
        prepareHeader
    );
    const displayConfig = getFunctionalTrainingDisplayConfig(settingData);
    
    const functionalTrainingSetting = {
        ...settingData,
        headers: mergedHeaders.headers,
        nyuShoDisplayConfig: displayConfig,
        sortConfig: displayConfig,
    };

    if (mergedHeaders.changed || isMissingFunctionalTrainingSortConfig(settingData)) {
        CommonHelper.setScreenConfigByOfficeCd(prepareFunctionalTrainingSettingForStorage(functionalTrainingSetting));
    }

    return functionalTrainingSetting;
}

export function getFunctionalTrainingHeaders(headers: any[], prepareHeader: PrepareHeaderFunction): any[] {
    return mergeFunctionalTrainingHeaders(headers, prepareHeader).headers;
}

export function getFunctionalTrainingDefaultConfig(): any {
    return { ...functionalTrainingGridConfig.defaultConfig };
}

export function prepareFunctionalTrainingSettingForStorage(config: any): any { // new config
    const storedConfig = getStoredScreenConfig() ?? {}; //Old config
    const { headers, sortConfig, nyuShoDisplayConfig, ...rootConfig } = config;
    const screenKey = LOCAL_STORAGE_KEY.settingDialogFunctionalTraining;
    const screenSetting = storedConfig[screenKey] ?? {};
    const functionalTrainingSortConfig = sortConfig ?? getDefaultFunctionalTrainingSortConfig();

    return {
        ...storedConfig,
        ...rootConfig,
        [screenKey]: createFunctionalTrainingStorageConfig(screenSetting, headers, functionalTrainingSortConfig),
    };
}

// Runtime setting merge helpers.
function mergeFunctionalTrainingHeaders(headers: any[], prepareHeader: PrepareHeaderFunction): any {
     const options:NormalizeSettingHeadersOptions =  {
            excludedFields: ['index'],
            prepareHeader,
    };
    return normalizeSettingHeaders(
        functionalTrainingGridConfig.header,
        headers,
        options
    );
}

function getFunctionalTrainingDisplayConfig(settingData: any): any {
    const commonConfig = getCommonNyuShoDisplayConfig(settingData?.nyuShoDisplayConfig);
    const functionalTrainingSortConfig = getFunctionalTrainingSortConfig(settingData) ?? getDefaultFunctionalTrainingSortConfig();

    return {
        ...commonConfig,
        ...functionalTrainingSortConfig,
    };
}

function isMissingFunctionalTrainingSortConfig(settingData: any): boolean {
    return !getFunctionalTrainingScreenSetting(settingData)?.sortConfig;
}

// Saved functional-training setting readers.
function getFunctionalTrainingScreenSetting(settingData: any): any {
    return settingData?.[LOCAL_STORAGE_KEY.settingDialogFunctionalTraining];
}

function getFunctionalTrainingSortConfig(settingData: any): any {
    const screenSetting = getFunctionalTrainingScreenSetting(settingData);
    const sortConfig = screenSetting?.sortConfig;
    if (!sortConfig) return null;

    return {
        ...(sortConfig.isSortByRoom !== undefined ? { isSortByRoom: sortConfig.isSortByRoom } : {}),
        ...(sortConfig.isSortByName !== undefined ? { isSortByName: sortConfig.isSortByName } : {}),
    };
}

// Storage preparation helpers.
function createFunctionalTrainingStorageConfig(screenSetting: any, headers: any[], sortConfig: any): any {
    return {
        ...(screenSetting ?? {}),
        headers,
        sortConfig: {
            isSortByRoom: sortConfig?.isSortByRoom ?? true,
            isSortByName: sortConfig?.isSortByName ?? false,
        },
    };
}

function getStoredScreenConfig(): any {
    try {
        return CommonHelper.getScreenConfigByOfficeCd();
    } catch (error) {
        return null;
    }
}


function getDefaultFunctionalTrainingSortConfig(): any {
    return {
        isSortByRoom: functionalTrainingGridConfig.defaultConfig.isSortByRoom,
        isSortByName: functionalTrainingGridConfig.defaultConfig.isSortByName,
    };
}

function getCommonNyuShoDisplayConfig(nyuShoDisplayConfig: any): any {
    return {
        ...(nyuShoDisplayConfig?.timeRangeSelected !== undefined ? { timeRangeSelected: nyuShoDisplayConfig.timeRangeSelected } : {}),
        ...(nyuShoDisplayConfig?.marginToTimeSelected !== undefined ? { marginToTimeSelected: nyuShoDisplayConfig.marginToTimeSelected } : {}),
        ...(nyuShoDisplayConfig?.unitSelected !== undefined ? { unitSelected: nyuShoDisplayConfig.unitSelected } : {}),
        ...(nyuShoDisplayConfig?.groupSelected !== undefined ? { groupSelected: nyuShoDisplayConfig.groupSelected } : {}),
    };
}
